import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadStates, LoadStatus, ThunkAction } from "../types/index.js";
import { insertScriptIntoHead } from "../utils/utils.js";
import { getAccommodation } from "./accommodation.js";
import { getColorSchemes } from "./colorSchemes.js";
import { getFonts } from "./fonts.js";
import { getIssues, getIssuesCheck } from "./issues.js";
import {
  getAllMedia,
  getAllMediaGroups,
  getMediaGroup,
} from "./mediaLibrary.js";
import { getActionModules, getSiteModules } from "./modules/thunks.js";
import { getPages } from "./pages.js";
import { getPartners } from "./partners.js";
import { getPrices } from "./prices.js";
import { getRevisions } from "./revisions.js";
import { getRooms } from "./rooms.js";
import { getSite } from "./sites.js";
import { getSpecials } from "./specials.js";

const initialState: LoadStates = {
  accommodation: {},
  colorSchemes: "unloaded",
  partners: "unloaded",
  mediaLibrary: {
    pictures: "unloaded",
    groups: "unloaded",
    picturesByGroupId: {},
  },
  pages: "unloaded",
  rooms: "unloaded",
  prices: "unloaded",
  site: "unloaded",
  specials: "unloaded",
  revisions: "unloaded",
  actionModules: "unloaded",
  siteModules: "unloaded",
  fonts: "unloaded",
  issues: { checker: "unloaded", issues: "unloaded" },
  scripts: {},
};

export const injectScript =
  (url: string): ThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const loadState = getState().loadStates.scripts[url];
    if (loadState === "loaded" || loadState === "loading") {
      return;
    }
    dispatch(loadScript({ url, status: "loading" }));
    try {
      await insertScriptIntoHead(url, document);
      dispatch(loadScript({ url, status: "loaded" }));
    } catch (error) {
      dispatch(loadScript({ url, status: "unloaded" }));
    }
  };

const loadStatesSlice = createSlice({
  name: "loadStates",
  initialState,
  reducers: {
    loadScript: (
      state,
      { payload }: PayloadAction<{ url: string; status: LoadStatus }>,
    ) => {
      state.scripts[payload.url] = payload.status;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPartners.pending, (state) => {
      state.partners = "loading";
    });

    builder.addCase(getPartners.fulfilled, (state) => {
      state.partners = "loaded";
    });

    builder.addCase(getPages.pending, (state) => {
      state.pages = "loading";
    });

    builder.addCase(getPages.fulfilled, (state) => {
      state.pages = "loaded";
    });

    builder.addCase(getAllMedia.pending, (state) => {
      state.mediaLibrary.pictures = "loading";
    });

    builder.addCase(getAllMedia.fulfilled, (state) => {
      state.mediaLibrary.pictures = "loaded";
    });

    builder.addCase(getAllMediaGroups.pending, (state) => {
      state.mediaLibrary.groups = "loading";
    });

    builder.addCase(getAllMediaGroups.fulfilled, (state) => {
      state.mediaLibrary.groups = "loaded";
    });

    builder.addCase(getMediaGroup.pending, (state, action) => {
      state.mediaLibrary.picturesByGroupId[action.meta.arg.groupId] = "loading";
    });

    builder.addCase(getMediaGroup.fulfilled, (state, action) => {
      state.mediaLibrary.picturesByGroupId[action.meta.arg.groupId] = "loaded";
    });

    builder.addCase(getColorSchemes.pending, (state) => {
      state.colorSchemes = "loading";
    });

    builder.addCase(getColorSchemes.fulfilled, (state) => {
      state.colorSchemes = "loaded";
    });

    builder.addCase(getSite.pending, (state) => {
      state.site = "loading";
    });

    builder.addCase(getSite.fulfilled, (state) => {
      state.site = "loaded";
    });

    builder.addCase(getSite.rejected, (state) => {
      state.site = "error";
    });

    builder.addCase(getSpecials.pending, (state) => {
      state.specials = "loading";
    });

    builder.addCase(getSpecials.fulfilled, (state) => {
      state.specials = "loaded";
    });

    builder.addCase(getRooms.pending, (state) => {
      state.rooms = "loading";
    });

    builder.addCase(getRooms.fulfilled, (state) => {
      state.rooms = "loaded";
    });

    builder.addCase(getPrices.pending, (state) => {
      state.prices = "loading";
    });

    builder.addCase(getPrices.fulfilled, (state) => {
      state.prices = "loaded";
    });

    builder.addCase(getAccommodation.pending, (state, action) => {
      state.accommodation[action.meta.arg.languageId] = "loading";
    });

    builder.addCase(getAccommodation.fulfilled, (state, action) => {
      state.accommodation[action.meta.arg.languageId] = "loaded";
    });

    builder.addCase(getRevisions.pending, (state) => {
      state.revisions = "loading";
    });

    builder.addCase(getRevisions.fulfilled, (state) => {
      state.revisions = "loaded";
    });

    builder.addCase(getActionModules.pending, (state) => {
      state.actionModules = "loading";
    });

    builder.addCase(getActionModules.fulfilled, (state) => {
      state.actionModules = "loaded";
    });

    builder.addCase(getSiteModules.pending, (state) => {
      state.siteModules = "loading";
    });

    builder.addCase(getSiteModules.fulfilled, (state) => {
      state.siteModules = "loaded";
    });

    builder.addCase(getFonts.pending, (state) => {
      state.fonts = "loading";
    });

    builder.addCase(getFonts.fulfilled, (state) => {
      state.fonts = "loaded";
    });

    builder.addCase(getIssues.pending, (state) => {
      state.issues.issues = "loading";
    });

    builder.addCase(getIssues.fulfilled, (state) => {
      state.issues.issues = "loaded";
    });

    builder.addCase(getIssuesCheck.pending, (state) => {
      state.issues.checker = "loading";
    });

    builder.addCase(getIssuesCheck.fulfilled, (state) => {
      state.issues.checker = "loaded";
    });
  },
});

export const { loadScript } = loadStatesSlice.actions;

export default loadStatesSlice.reducer;
